import React, { useEffect, useRef } from "react";
import { TradeInAemLabel } from "../../../utils/labels";
import { TradeInAemModelSelectType, tradeInAemModelSelectTypesList } from "../../../utils/constants";
import TradeInAemModelSelectCtaBlock from "./TradeInAemModelSelectCtaBlock";
import TradeInAemModelSelectList from "./TradeInAemModelSelectList";
import { type TradeInAemDispatchType, useTradeInAemSelector } from "../../../redux/store";
import * as Styles from "../common/styles/TradeInAem";
import * as ModelSelect from "./styles/ModelSelect";
import * as Button from "../../../../common-deprecated/styles/v2/toyota/components/Button";
import * as Text from "../../../../common-deprecated/styles/v2/toyota/utilities/Text";
import { IconWrapper } from "../../../../common-deprecated/styles/v2/toyota/globals/IconWrapper";
import Icon from "../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import { useBreakpoint } from "../../../../common-deprecated/themes/common";
import type { TradeInAemModelSelectReducerType } from "../../../redux/reducers/TradeInAemModelSelectReducer";
import { getInternalLink } from "../../../utils/navUtils";
import { useTradeInCsl } from "../../../csl/tradeInCslUtils";
import { trackStepModelSelect } from "../../../utils/tracking";
import { tradeInAemModelSelectSelectCarType } from "../../../redux/actions/TradeInAemModelSelectActions";
import { tradeInAemUpdateExtraInfoProp } from "../../../redux/actions/TradeInAemVehicleActions";
import useTrackingParams from "../../../hooks/useTrackingParams";
import { useDispatch } from "../../../../shared-logic/redux/store";
import { showBackCtaOnStepComponent } from "../../../utils/utils";

const TradeInAemModelSelectContainer = (): JSX.Element => {
    const dispatch = useDispatch<TradeInAemDispatchType>();
    const csl = useTradeInCsl();
    const currentStep = useTradeInAemSelector((state) => state.tradeInAemNav.step);
    const commonSettings = useTradeInAemSelector((state) => state.commonSettings);
    const selectedCarType = useTradeInAemSelector((state) => state.tradeInAemModelSelect.selectedCarType);
    const modelSelectBlocks = useTradeInAemSelector((state) => state.tradeInAemSettings.modelSelectBlocks);

    const prevStep = getInternalLink(csl, currentStep, dispatch, commonSettings, "prev");
    const nextStep = getInternalLink(csl, currentStep, dispatch, commonSettings, "next");

    // On mobile, scroll down to the car list when a new/stock car is selected.
    const isMobile = useBreakpoint("down", "sm");
    const previousSelected = useRef<TradeInAemModelSelectReducerType["selectedCarType"]>(null);
    const dividerRef = useRef<HTMLDivElement>(null);
    const trackingParams = useTrackingParams();

    useEffect(() => {
        if (
            isMobile &&
            previousSelected.current !== selectedCarType &&
            selectedCarType !== TradeInAemModelSelectType.Used
        ) {
            dividerRef.current?.scrollIntoView({ behavior: "smooth" });
        }
        previousSelected.current = selectedCarType;
    }, [selectedCarType]);

    useEffect(() => {
        trackStepModelSelect(trackingParams);
    }, []);

    return (
        <Styles.Main>
            <Styles.Container large>
                <ModelSelect.SelectGrid>
                    {/* Currently, the order of the block is hardcoded based on tradeInAemModelSelectTypesList
                        If we every get the requirement they want to choose the order. We can switch modelSelectBlocks with tradeInAemModelSelectTypesList.
                        In i18n the order of 'modelSelectBlocks' doesn't match tradeInAemModelSelectTypesList. So this would require manual updates in i18n.
                    */}
                    {tradeInAemModelSelectTypesList
                        .filter((modelSelectType) => modelSelectBlocks.includes(modelSelectType))
                        .map((modelSelectType) => (
                            <>
                                <TradeInAemModelSelectCtaBlock carType={modelSelectType} />
                                {selectedCarType === modelSelectType && <TradeInAemModelSelectList />}
                            </>
                        ))}
                </ModelSelect.SelectGrid>

                <Styles.ButtonWrapper stretchMobile spaceBetween>
                    {showBackCtaOnStepComponent(currentStep, csl.enabledNavSteps) && (
                        <Button.Link m={0} as="a" href={prevStep.href} onClick={prevStep.onClick} hasIcon>
                            <IconWrapper>
                                <Icon variant="arrowLeft" />
                            </IconWrapper>
                            <Text.Button>
                                <TradeInAemLabel label="tradeInAemBack" />
                            </Text.Button>
                        </Button.Link>
                    )}

                    <Button.Link
                        m={0}
                        as="a"
                        href={nextStep.href}
                        onClick={() => {
                            dispatch(tradeInAemModelSelectSelectCarType(null));
                            dispatch(tradeInAemUpdateExtraInfoProp("selectedCarType", null));
                            if (nextStep.onClick) nextStep.onClick();
                        }}
                        hasIconRight
                    >
                        <Text.Button>
                            <TradeInAemLabel label="tradeInAemModelSelectSkip" />
                        </Text.Button>
                        <IconWrapper>
                            <Icon variant="arrowRight" />
                        </IconWrapper>
                    </Button.Link>
                </Styles.ButtonWrapper>
            </Styles.Container>
        </Styles.Main>
    );
};

export default TradeInAemModelSelectContainer;
